import React, { useState } from 'react'

import { SubmitHandler, useForm } from 'react-hook-form'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Switch,
  Text,
} from '@chakra-ui/react'

import {
  useEditCustomSummaryPrompt,
  useGenerateSummaryPart,
} from '@/react-query/interview/interview'
import { AutoResizeTextarea } from '@/views/components/AutoResizeTextarea/AutoResizeTextarea'

type EditSummaryPromptProps = {
  id: string
  partId: string
  name: string
  text: string
  hasScoring: boolean
  setIsPromptEditing: React.Dispatch<React.SetStateAction<boolean>>
}

type EditSummaryPromptForm = {
  name: string
  text: string
}

export const EditSummaryPrompt: React.FC<EditSummaryPromptProps> = ({
  partId,
  id,
  name,
  text,
  hasScoring: initialHasScoring,
  setIsPromptEditing,
}) => {
  const editCustomSummaryPrompt = useEditCustomSummaryPrompt()
  const generateSummaryPart = useGenerateSummaryPart()
  const [hasScoring, setHasScoring] = useState(initialHasScoring)

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    register,
  } = useForm<EditSummaryPromptForm>({
    defaultValues: {
      name,
      text,
    },
  })

  const onSubmit: SubmitHandler<EditSummaryPromptForm> = async ({
    name,
    text,
  }) => {
    const scoringWasEnabled = !initialHasScoring && hasScoring

    await editCustomSummaryPrompt.mutateAsync({
      partId,
      data: {
        name,
        text,
        hasScoring,
      },
    })

    if (scoringWasEnabled) {
      await generateSummaryPart.mutateAsync({
        customSummaryPartPromptId: id,
      })
    }

    setIsPromptEditing(false)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Text as={'h2'} fontWeight="bold" fontSize="1.25rem">
        Edit topic prompt
      </Text>

      <Flex mt={5} gap={8} alignItems={'center'}>
        <FormControl isInvalid={Boolean(errors?.name)} maxW={'300px'}>
          <Input
            id="topic-name"
            placeholder={'Card title'}
            variant={'brand-solid-white'}
            {...register('name', {
              required: 'Card title is required',
            })}
          />
          <FormErrorMessage>
            {errors.name && errors.name.message}
          </FormErrorMessage>
        </FormControl>

        <Flex alignItems={'center'} gap={3} flexShrink={0}>
          <Switch
            id="edit-scoring"
            isChecked={hasScoring}
            onChange={(event) => {
              setHasScoring(event.target.checked)
            }}
            size={'lg'}
            variant={'brand-black'}
          />
          <FormLabel
            htmlFor="edit-scoring"
            mb={0}
            cursor={'pointer'}
            display={'inline-flex'}
            lineHeight={1}
          >
            Add scoring
          </FormLabel>
        </Flex>
      </Flex>

      <FormControl isInvalid={Boolean(errors?.text)} mt={5}>
        <AutoResizeTextarea
          px="1.25rem"
          py="0.65rem"
          rounded={'1.25rem'}
          flexGrow={1}
          variant={'brand-solid-white'}
          placeholder={
            'Please describe what would you like AI to report on here'
          }
          minRows={5}
          {...register('text', {
            required: 'Topic description is required',
          })}
        />
        <FormErrorMessage>
          {errors.text && errors.text.message}
        </FormErrorMessage>
      </FormControl>

      <Box mt={5}>
        <Button
          w={'fit-content'}
          variant={'brand-solid'}
          isLoading={isSubmitting}
          type="submit"
          isDisabled={!isValid}
        >
          Update
        </Button>
        <Button
          w={'fit-content'}
          variant={'brand-solid-white'}
          ml={3}
          onClick={() => {
            setIsPromptEditing(false)
          }}
        >
          Cancel
        </Button>
      </Box>
    </form>
  )
}
